// Single source of truth for all financial data per resort.
//
// CONSOB COMPLIANCE NOTES:
// - Non vengono mai esposti target di IRR/Cash Yield "puntuali" come promessa.
// - Le percentuali presenti negli `scenarios.*.annualYieldPct` sono parametri
//   di simulazione editabili nel calcolatore (ipotesi modificabili dall'utente),
//   non target di prodotto. Il calcolatore lo segnala esplicitamente.
// - I valori in `kpi.*` sono dati strutturali del progetto (CAPEX, EBITDA
//   stabilizzato, margin) — non costituiscono promessa di rendimento al socio.
// - La pubblicazione completa dei project financing ufficiali avviene solo
//   in fase di qualifica, con disclaimer integrato nel documento.

// Shared payment plan for Real Estate (Albania & Indonesia)
// 10% May 2026 | 50% in 16 monthly installments (Jun'26 – Sep'27) | 40% Oct 2027 | Grand opening Dec 2028
export const PAYMENT_PLAN = {
  reservation: { label: 'Maggio 2026', pct: 10 },
  installments: {
    startLabel: "Giugno 2026",
    endLabel: 'Settembre 2027',
    months: 16,
    totalPct: 50,
  },
  balloon: { label: 'Ottobre 2027', pct: 40 },
  grandOpening: 'Q4 2027',
};

// Apartment types and pricing from the official IT brochure V2 (May 2026).
// Valid for Albania/Spille and Indonesia/Lombok — same architectural grid.
export const APARTMENT_TYPES = [
  { key: 'standard',  name: 'Standard Suite',  sqm: 82.5,  pricePerSqm: 2500, price: 152500, description: 'Lusso compatto — ingresso strategico, ideale per soggiorni vacanza.' },
  { key: 'king',      name: 'King Suite',      sqm: 102.3, pricePerSqm: 2500, price: 222500, description: 'Due camere open-space — versatile per uso residenziale o a reddito.' },
  { key: 'king-pool', name: 'King Pool Suite', sqm: 102.3, pricePerSqm: 2700, price: 283500, description: 'Due camere + piscina privata — flusso indoor/outdoor armonioso.' },
  { key: 'rooftop',   name: 'Rooftop Suite',   sqm: 238.5, pricePerSqm: 3000, price: 615000, description: 'Quattro camere master — cucina a isola, doppi bagni, privacy elevata.' },
];

// ★ 2026-07-29 — Chumphon pre-launch promo: sconto -30% rispetto ai prezzi
// standard degli altri Green Paradise (Lombok/Spille), valido fino a ottobre 2026.
// Applicato uniformemente sia a Quote Equity Share sia a Real Estate.
const CHUMPHON_DISCOUNT_PCT = 30;
const applyChumphonDiscount = (price) => Math.round(price * (100 - CHUMPHON_DISCOUNT_PCT) / 100);

export const APARTMENT_TYPES_CHUMPHON = APARTMENT_TYPES.map((t) => ({
  ...t,
  price: applyChumphonDiscount(t.price),
  pricePerSqm: applyChumphonDiscount(t.pricePerSqm),
  originalPrice: t.price,
  originalPricePerSqm: t.pricePerSqm,
  discountPct: CHUMPHON_DISCOUNT_PCT,
  description: `${t.description} · Promo pre-launch -${CHUMPHON_DISCOUNT_PCT}%.`,
}));

// Resort-level financial blueprints.
//
// CONSOB CLEANUP (2026-05): rimossi `kpi.targetCashYield` e `kpi.irrLevered`.
// Restano solo dati strutturali (CAPEX, EBITDA stabilizzato, margin, yieldOnCost).
// Gli `scenarios` mantengono percentuali di rendimento perché sono assunzioni
// editabili nel calcolatore — NON sono target di prodotto.
export const RESORT_FINANCIALS = {
  spille: {
    name: 'Green Paradise Spille',
    country: 'Albania',
    flag: '🇦🇱',
    grandOpening: 'Q4 2027',
    capex: '€29,7M',
    totalUnits: 80,
    // ★ 2026-07-29 — Spille Real Estate SOLD OUT (0 appartamenti disponibili)
    blocks: { total: 10, available: 0, perBlock: '2 King Pool · 2 King · 3 Standard · 1 Rooftop', soldOut: true },
    units: { total: 80, available: 0 }, // Sold out — nessun appartamento disponibile
    // ★ 2026-08-13 (Fase 51 · Compliance CONSOB) — Equity DISABILITATO su richiesta utente
    equity: { available: false, hiddenFromUi: true, _legacy: {
      quotaPrice: 40000,
      totalQuotas: 99,
      availableQuotas: 47,
      benefitsLastN: 20,
      horizon: '8–10 anni (indicativo)',
      kpi: {
        ebitdaStabilizzato: '€2,08M',
        margin: '40%',
        yieldOnCost: '7%',
      },
    } },
    // ★ 2026-07-29 — Spille RE è sold out.
    realEstate: { available: false, startingFrom: 152500, types: APARTMENT_TYPES, soldOut: true, note: 'Real Estate esaurito: 80/80 appartamenti già assegnati. Nuove disponibilità in comunicazione.' },
  },
  lombok: {
    name: 'Green Paradise Lombok',
    country: 'Indonesia',
    flag: '🇮🇩',
    grandOpening: 'Q4 2027',
    capex: '€52,9M',
    // ★ 2026-08-13 (Fase 50) — Nuovo import ufficiale da Google Sheet:
    //   220 unità totali su 22 blocchi × 10, di cui 100 attualmente DISPONIBILI
    //   e 120 già VENDUTE (blocchi 1-6, 10-12, 17-19 completi rossi nel Sheet).
    //   Prezzo €169.100 – €220.500 · Piano pagamenti caparra + anticipo + 12 rate + saldo.
    //   ★ 2026-08-14 (Fase 52) — RIALLINEAMENTO PDF UFFICIALE del cliente:
    //   14 blocchi × 10 unità = 140 unità totali, tutte disponibili di default.
    //   Prezzo €169.100 – €220.500 · Piano pagamenti caparra + anticipo 30% + 12 rate + saldo 20%.
    totalUnits: 140,
    blocks: { total: 14, available: 14, perBlock: '10 unità (2 al piano 0, 3 al piano 1, 3 al piano 2, 2 al piano 3)' },
    units: { total: 140, available: 140, sold: 0 },
    // ★ 2026-08-13 (Fase 50) — Equity self-service DISABILITATO su richiesta utente.
    //   La possibilità di diventare SOCIO da €70.000 rimane, ma solo su call
    //   con il Senior Indonesia o il CEO (gestita nella sezione "Diventa socio").
    equity: { available: false, hiddenFromUi: true },
    equitySocio: {
      available: true,             // sezione dedicata (call gated)
      quotaFromEur: 70000,
      capitaleSocialeEur: 52500000,
      shareCompany: 'PT Green Paradise Socio (Indonesia)',
      note: 'Documentazione societaria disponibile su richiesta dopo call informativa con il team senior.',
    },
    realEstate: {
      available: true,
      startingFrom: 169100,
      types: APARTMENT_TYPES,
      note: 'Piano pagamenti: caparra + anticipo 30% + 12 rate mensili + saldo 20% alla consegna 2027.',
      paymentPlan: {
        title: 'Piano pagamenti 12 rate mensili',
        steps: [
          { label: 'Caparra confirmatoria', amount: 10670 },
          { label: 'Anticipo alla firma (30%)', pct: 30 },
          { label: '12 rate mensili', pct: 50, note: '≈€7.045–€9.187/mese' },
          { label: 'Saldo alla consegna 2027 (20%)', pct: 20 },
        ],
      },
      // ★ 2026-08-14 (Fase 51) — ROI indicativo Lombok su richiesta utente.
      //   12% annuo NETTO come proiezione locativa attesa dalla consegna.
      //   NON garantito — la comunicazione è sempre "indicativo".
      roi: {
        annual: 12,
        guaranteed: false,
        note: 'Proiezione indicativa, non garantita.',
      },
    },
    // Nessuna brochure/project financing pubblicati (in aggiornamento — Fase 50 richiesta utente)
    documents: {},
  },
  chumphon: {
    name: 'Green Paradise Chumphon',
    country: 'Thailandia',
    flag: '🇹🇭',
    grandOpening: 'Q4 2027',
    // ★ 2026-07-29 — Chumphon rebranding pre-launch:
    //   • Real Estate ORA APERTO (era solo Equity) con inventario 70/115
    //   • Quote Equity: 50/99 disponibili a €30.000 (nuovo pricing)
    //   • Promo pre-launch: TUTTI i prezzi scontati -30% fino a ottobre 2026
    totalUnits: 115,
    blocks: { total: 23, available: 14, perBlock: '2 King Pool · 2 King · 3 Standard · 1 Rooftop' }, // 23 × 5 = 115 unità; 14 × 5 = 70 disp
    units: { total: 115, available: 70 },
    equityOnly: false, // Real Estate ora disponibile in pre-launch
    // ★ NEW — Promo pre-launch: -30% su tutti i prezzi fino a ottobre 2026
    promo: {
      active: true,
      discountPct: 30,
      endDate: '2026-10-31',
      endLabel: 'Ottobre 2026',
      shortLabel: '-30% pre-launch',
      description: 'Promo pre-launch: tutti i prezzi Chumphon Real Estate sono scontati del 30% rispetto agli altri Green Paradise fino a ottobre 2026.',
    },
    // ★ 2026-08-13 (Fase 51 · Compliance CONSOB) — Equity DISABILITATO su richiesta utente
    equity: { available: false, hiddenFromUi: true, _legacy: {
      quotaPrice: 30000,
      originalQuotaPrice: 42857,
      totalQuotas: 99,
      availableQuotas: 50,
      benefitsLastN: 20,
    } },
    realEstate: {
      available: true,
      // Standard Suite Chumphon: €152.500 × 0.7 = €106.750
      startingFrom: 106750,
      originalStartingFrom: 152500,
      types: APARTMENT_TYPES_CHUMPHON,
      note: 'Promo pre-launch: -30% su tutti i tagli fino a ottobre 2026.',
    },
  },
  // ★ 2026-08-11 (Fase 37) — La Mar Residence: nuovo progetto Real Estate in Albania.
  //   Solo Real Estate (no equity share), 21 unità in 3 blocchi (A/B/C).
  //   Prezzo da €80.554 · ROI netto annuo 5% · Consegna 2027.
  //   Pagamento: 5 rate mensili (caparra + anticipo + 2 rate + saldo consegna).
  'la-mar-residence': {
    name: 'La Mar Residence',
    country: 'Albania',
    flag: '🇦🇱',
    grandOpening: '2027',
    location: 'Golem, Albania',
    developer: 'HCF Property',
    totalUnits: 21,
    blocks: { total: 3, available: 3, perBlock: '1 unità 60mq + 6 unità 53,8mq' },
    units: { total: 21, available: 21 },
    equity: { available: false },
    realEstate: {
      available: true,
      startingFrom: 80554,
      note: 'Bilocali fronte mare · Pagamento 5 rate mensili + saldo alla consegna 2027.',
      paymentPlan: {
        title: 'Piano pagamenti 5×5 mesi + saldo',
        steps: [
          { label: 'Caparra confirmatoria', amount: 5000 },
          { label: 'Anticipo alla firma (mese 1)', pct: 55 },
          { label: 'Rata 2 (mese 2-3)', pct: 15 },
          { label: 'Rata 3 (mese 4-5)', pct: 15 },
          { label: 'Saldo alla consegna 2027', pct: 8 },
        ],
      },
      // ★ 2026-08-14 (Fase 51 compliance) — Il ROI viene mostrato nell'hero
      //   solo se `roi.annual` è truthy. Rimosso su richiesta utente per
      //   evitare qualsiasi comunicazione di "rendimento garantito" in facciata.
      roi: null,
    },
    // Documenti Drive (public) — condivisi tra tutte le unità
    documents: {
      brochure: 'https://drive.google.com/file/d/15dYUDrIQ5KPqwPpONKjrYgVQP8JIokxq/view?usp=sharing',
      permessoCostruzione: 'https://drive.google.com/file/d/1R-SBctw7_67AG1P5JFuer3ZahGrsjUYR/view?usp=drive_link',
      fotoProgetto: 'https://drive.google.com/drive/folders/1e7-ntfRco6cc-kLOAhQtbCthIPSnNPbG?usp=drive_link',
    },
  },
  // ★ 2026-08-12 (Fase 43) — Narea: nuovo progetto Real Estate in Albania.
  //   Solo Real Estate (no equity share), 161 unità · 14 piani · blocco unico B.
  //   Prezzo da €134.420 · ROI netto annuo 5% · Consegna 2029.
  //   Pagamento: Caparra €15k + Anticipo + 5 rate mensili + Saldo consegna.
  'narea': {
    name: 'Narea',
    country: 'Albania',
    flag: '🇦🇱',
    grandOpening: '2029',
    location: 'Querret, Albania',
    developer: 'HCF Property',
    totalUnits: 161,
    blocks: { total: 1, available: 1, perBlock: '14 piani · 161 appartamenti (bilocali, trilocali, attici)' },
    units: { total: 161, available: 53 },
    equity: { available: false },
    realEstate: {
      available: true,
      startingFrom: 134420,
      note: 'Landmark residenziale · 14 piani · Pagamento in 5 rate mensili + saldo alla consegna 2029.',
      paymentPlan: {
        title: 'Piano pagamenti 5 rate mensili + saldo',
        steps: [
          { label: 'Caparra confirmatoria', amount: 15000 },
          { label: 'Anticipo alla firma', pct: 40 },
          { label: '5 rate mensili', pct: 45 },
          { label: 'Saldo alla consegna 2029', pct: 15 },
        ],
      },
      // ★ 2026-08-14 (Fase 51 compliance) — ROI rimosso dalla hero.
      //   Nessun rendimento "garantito" comunicato in facciata.
      roi: null,
    },
    // Documenti Drive (public) — condivisi tra tutte le unità Narea
    documents: {
      brochure: 'https://drive.google.com/file/d/1GmLZI0L2J5dYtRkLeOmtapj1UG0WiCE3/view?usp=drive_link',
      permessoCostruzione: 'https://drive.google.com/drive/folders/163X-8zoTfoggdQ_JKblj9v_mhDg4J9D9?usp=drive_link',
      fotoProgetto: 'https://drive.google.com/drive/folders/1f9Ol-QWSJvhssL-oPi_2lkCQV8fKTbe1?usp=drive_link',
    },
  },
  // ★ 2026-08-28 — Palm Paradise: nuovo progetto Real Estate in Albania (Querret).
  //   Solo Real Estate (no equity share), 198 unità · 4 blocchi (A1/C1/C4/C5).
  //   Prezzo da €120.919 · ROI netto annuo 5% · Consegna Q2 2027.
  //   Pagamento (come da tabella ufficiale): Caparra €5k + Anticipo + Rata + Saldo.
  'palm-paradise': {
    name: 'Palm Paradise',
    country: 'Albania',
    flag: '🇦🇱',
    grandOpening: 'Q2 2027',
    location: 'Querret, Albania',
    developer: 'HCF Property',
    totalUnits: 198,
    blocks: { total: 4, available: 4, perBlock: '4 blocchi (A1 · C1 · C4 · C5) · 5-6 piani ciascuno' },
    units: { total: 198, available: 35 },
    equity: { available: false },
    realEstate: {
      available: true,
      startingFrom: 120919,
      note: 'Quartiere residenziale tropicale · 4 blocchi · Pagamento anticipo + rata + saldo alla consegna Q2 2027.',
      paymentPlan: {
        title: 'Piano pagamenti anticipo + rata + saldo',
        steps: [
          { label: 'Caparra confirmatoria', amount: 5000 },
          { label: 'Anticipo alla firma', pct: 35 },
          { label: 'Rata intermedia', pct: 31 },
          { label: 'Saldo alla consegna Q2 2027', pct: 8 },
        ],
      },
      // ★ Compliance — nessun rendimento "garantito" comunicato in facciata.
      roi: null,
    },
    // Documenti Drive (public) — condivisi tra tutte le unità Palm Paradise
    documents: {
      brochure: 'https://drive.google.com/file/d/19d-hbsF2CDUeekiwq5Qc6jJGPBMGcb-T/view?usp=drive_link',
      permessoCostruzione: 'https://drive.google.com/drive/folders/1JJKScdc97TJ8f__npfJWf5q5ppBlt3hX?usp=drive_link',
      fotoProgetto: 'https://drive.google.com/drive/folders/1dzh3E2k-LscY0PO0ZGrRs_JfX8cBb1MT?usp=drive_link',
    },
  },
  // ★ 2026-08-29 — Vista Mare: nuovo Residence in Albania (Querret).
  //   Real Estate puro, 189 unità · 7 piani · consegna Aprile 2027.
  //   Pagamento (da tabella): Caparra 3% + Anticipo 30% + 5 rate + Saldo.
  'vista-mare': {
    name: 'Vista Mare',
    country: 'Albania',
    flag: '🇦🇱',
    grandOpening: 'Aprile 2027',
    location: 'Querret, Albania',
    developer: 'HCF Property',
    totalUnits: 189,
    blocks: { total: 1, available: 1, perBlock: 'Residence unico · 7 piani (0-6)' },
    units: { total: 189, available: 6 },
    equity: { available: false },
    realEstate: {
      available: true,
      startingFrom: 56800,
      note: 'Residence vista Adriatico a 2 min dalla spiaggia · Pagamento caparra 3% + anticipo 30% + 5 rate + saldo alla consegna Aprile 2027.',
      paymentPlan: {
        title: 'Piano pagamenti 5 rate + saldo',
        steps: [
          { label: 'Caparra confirmatoria', pct: 3 },
          { label: 'Anticipo alla firma', pct: 30 },
          { label: '5 rate mensili (cad.)', pct: 10 },
          { label: 'Saldo alla consegna Aprile 2027', pct: 10 },
        ],
      },
      // ★ Compliance — nessun rendimento "garantito" comunicato in facciata.
      roi: null,
    },
    // Documenti Drive (public) — condivisi tra tutte le unità Vista Mare
    documents: {
      brochure: 'https://drive.google.com/file/d/1cBRtyKdhpnNpxkTkiLMZKPYPGvYDoGMq/view?usp=drive_link',
      permessoCostruzione: 'https://drive.google.com/file/d/1zhuirLq5L1XqvDwjSrSt0ikZuZ5iTeNu/view?usp=drive_link',
      fotoProgetto: 'https://drive.google.com/drive/folders/1t0APiUmAPd9Ejvou2ckNMPSLV6CGlad-?usp=drive_link',
    },
  },
  // ★ 2026-08-29 — Green Paradise Lombok: resort ufficiale HCF a Bumbang Bay.
  //   Real Estate puro: 29 ville (14 Green + 15 Paradise) · ROI 15% annuo ·
  //   caparra + anticipo + 5 rate + saldo · Apertura Ottobre 2027.
  'green-paradise-lombok': {
    name: 'Green Paradise Lombok',
    country: 'Indonesia',
    flag: '🇮🇩',
    grandOpening: 'Ottobre 2027',
    location: 'Bumbang Bay, Lombok, Indonesia',
    developer: 'HCF Property',
    totalUnits: 29,
    blocks: { total: 2, available: 2, perBlock: '14 Villa Green + 15 Villa Paradise' },
    units: { total: 29, available: 29 },
    equity: { available: false },
    realEstate: {
      available: true,
      startingFrom: 116000,
      note: 'Resort eco-luxury fronte oceano con spiaggia privata · Pagamento caparra + anticipo + 5 rate + saldo · Apertura Ottobre 2027.',
      paymentPlan: {
        title: 'Piano pagamenti 5 rate + saldo',
        steps: [
          { label: 'Caparra confirmatoria', pct: 7 },
          { label: 'Anticipo alla firma', pct: 25 },
          { label: '5 rate (cad.)', pct: 10 },
          { label: 'Saldo alla consegna', pct: 20 },
        ],
      },
      // ★ Compliance — nessun rendimento "garantito" comunicato in facciata.
      roi: null,
    },
    // Documenti (public) — brochure ufficiale + cartelle ville
    documents: {
      brochure: 'https://drive.google.com/file/d/1NPv4YnWqI4Q4FoUeuC3iQoUWyWa7wNTA/view?usp=sharing',
      permessoCostruzione: null,
      fotoProgetto: 'https://drive.google.com/drive/folders/1mjabd2gsBVPnchlAgFWrtK3ycJtxO_Sv?usp=drive_link',
    },
  },
};

// Global stats — based on apartment-level inventory (3 resort)
// ★ 2026-07-29 updated:
//   • Lombok: 20 blocchi × 8 unità = 160 (12 disponibili)
//   • Spille: 10 blocchi × 8 unità = 80 (SOLD OUT — 0 disponibili)
//   • Chumphon: 23 blocchi × 5 unità = 115 (70 disponibili, ora anche RE aperto)
export const GLOBAL_STATS = {
  totalResorts: 3,
  totalCountries: 3,
  totalUnits: 355,           // 160 + 80 + 115
  totalBlocks: 53,           // 20 + 10 + 23
  availableBlocks: 16,       // 2 + 0 + 14
  realEstateAvailable: 82,   // Lombok 12 + Spille 0 + Chumphon 70
  minDeposit: 15500,
  // ★ Equity aggregate — 3 resort × 99 quote totali = 297
  //   disponibili: Lombok 17 + Spille 47 + Chumphon 50 = 114
  totalEquityQuotas: 297,
  availableEquityQuotas: 114,
};

// Benefit esclusivi per gli ultimi 20 soci di ogni resort
export const LAST20_BENEFITS = [
  {
    icon: 'Camera',
    title: 'Telecamere 24/7 Live',
    desc: 'Accesso illimitato al live feed del resort, ovunque ti trovi.',
    valueHint: '€600/anno di valore percepito',
  },
  {
    icon: 'Plane',
    title: 'Viaggio Annuale 7 Notti',
    desc: 'All inclusive (volo escluso), una volta l’anno finché resti socio.',
    valueHint: '€3.500/anno di valore',
  },
  {
    icon: 'Star',
    title: 'Priorità Soci',
    desc: 'Prenotazioni prioritarie, eventi esclusivi, concierge dedicato.',
    valueHint: 'Priceless',
  },
];

// Manifesto testi dalle brochure ufficiali
export const MANIFESTO = {
  headline: 'Dove il paradiso tranquillo diventa casa.',
  subheadline: 'La serenità non dovrebbe essere una fuga. Dovrebbe essere una residenza.',
  pillars: [
    { title: 'Architettura guidata dal paesaggio', desc: 'Ogni orientamento offre prospettive sul mare, sulle montagne o sull’acqua.' },
    { title: 'Urbanistica a bassa densità', desc: 'Privacy residenziale e ritmo umano. La luce diventa spazio, lo spazio diventa serenità.' },
    { title: 'Vita integrata con il resort', desc: 'Servizi hotel-grade, amenities full-time, hospitality quotidiana senza sforzo.' },
    { title: 'Esecuzione disciplinata', desc: 'Terreno acquisito, masterplan completato, cantieri per fasi con team eco-specialistici.' },
  ],
  claim: 'Un indirizzo modellato dal paesaggio. Una residenza sorretta dalla disciplina. Una destinazione che entra nel suo momento.',
};

// Exit strategy: i soci possono cedere la quota equity o l'immobile sul
// marketplace interno HCF Estate (hcf.estate) come da contratto sociale /
// statuto. Mercato secondario controllato via KYC e approval del board.
export const EXIT_STRATEGY = {
  marketplaceUrl: 'https://hcf.estate',
  marketplaceLabel: 'HCF Estate Marketplace',
  headline: 'Exit strategy via hcf.estate',
  description: 'Le quote Equity e gli immobili possono essere ceduti sul marketplace interno HCF Estate come da contratto sociale e statuto della società. Mercato secondario controllato con KYC del subentrante e approval del board, alle condizioni di mercato.',
};

// CONSOB disclaimer riutilizzabile in qualunque componente che mostra dati
// finanziari/economici. Da usare sempre vicino a numeri o percentuali.
export const CONSOB_DISCLAIMER = "Le informazioni e le eventuali proiezioni hanno carattere indicativo e non costituiscono offerta al pubblico, consulenza finanziaria o promessa di rendimento. Il risultato effettivo dipende da variabili di mercato e dai documenti ufficiali della singola SPV.";

// Helpers
export const fmtEUR = (n, digits = 0) => {
  if (n == null || isNaN(n)) return '—';
  return '€' + Number(n).toLocaleString('it-IT', { maximumFractionDigits: digits });
};
export const fmtEURk = (n) => {
  if (n == null) return '—';
  return '€' + (n / 1000).toFixed(n >= 100000 ? 0 : 1) + 'k';
};
